/**
 * Formato y vocabulario de cifras — sección 8.3 de la especificación.
 *
 * POR QUÉ NO SE USA `Intl`
 *   Las reglas de la sección 8.3 son pocas y muy concretas (millones con una decimal, millares
 *   con `k`, signo en las variaciones). `Intl.NumberFormat` daría «12.300.000 €» y habría que
 *   deshacerlo a mano; además su salida depende de la versión de ICU del equipo. Aquí el
 *   resultado es el mismo en cualquier máquina y en cualquier navegador, que es lo que las
 *   pruebas de aceptación comprueban letra a letra.
 *
 * QUÉ UNIDADES ESPERA CADA FUNCIÓN
 *   `money` y `delta` reciben **euros** (`price`, `priceIncrement`, `budget` de la sección 9.2).
 *   `percent` y `percentDelta` reciben **proporciones** 0–1 (`availability`, `trend7d`…, que el
 *   motor de métricas guarda como variación relativa, no como porcentaje ya multiplicado).
 */

/** Dato ausente: una raya, nunca un 0 ni un hueco en blanco (sección 8.3). */
export const DASH = "—";

const EUROS_POR_MILLON = 1_000_000;
const EUROS_POR_MILLAR = 1_000;

/** Coma decimal española sin depender del entorno. */
function conComa(value: number, decimales: number): string {
  return value.toFixed(decimales).replace(".", ",");
}

/**
 * Entero con punto de millar: `412` · `1.234`.
 * Sirve para los puntos totales y para cualquier recuento de la interfaz.
 */
export function count(value: number): string {
  if (!Number.isFinite(value)) return DASH;
  const entero = Math.round(value);
  const signo = entero < 0 ? "-" : "";
  return (
    signo +
    Math.abs(entero)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  );
}

/**
 * Dinero: `12,3 M` · `850 k` · `500 €`.
 *
 * El salto de `k` a `M` se decide **después** de redondear los millares, para que 999.600 €
 * salga como `1,0 M` y no como el poco creíble `1000 k`.
 */
export function money(euros: number): string {
  if (!Number.isFinite(euros)) return DASH;
  const abs = Math.abs(euros);
  const millares = Math.round(euros / EUROS_POR_MILLAR);

  if (abs >= EUROS_POR_MILLON || Math.abs(millares) >= 1_000) {
    return `${conComa(euros / EUROS_POR_MILLON, 1)} M`;
  }
  if (abs >= EUROS_POR_MILLAR) return `${count(millares)} k`;
  return `${count(euros)} €`;
}

/** Variación de dinero, siempre con signo: `+1,2 M` · `-400 k`. */
export function delta(euros: number): string {
  if (!Number.isFinite(euros)) return DASH;
  const texto = money(euros);
  return euros > 0 ? `+${texto}` : texto;
}

/** Puntos, con una decimal: `6,2`. */
export function points(value: number): string {
  if (!Number.isFinite(value)) return DASH;
  return conComa(value, 1);
}

/** Variación de puntos, con signo: `+4,1`. */
export function pointsDelta(value: number): string {
  if (!Number.isFinite(value)) return DASH;
  return value > 0 ? `+${points(value)}` : points(value);
}

/** Cifra decimal suelta —eficiencia, pesos del modelo—: `0,63`. */
export function decimal(value: number, decimales = 2): string {
  if (!Number.isFinite(value)) return DASH;
  return conComa(value, decimales);
}

/** Proporción 0–1 como porcentaje sin signo: `88,0 %`. */
export function percent(proporcion: number, decimales = 1): string {
  if (!Number.isFinite(proporcion)) return DASH;
  return `${conComa(proporcion * 100, decimales)} %`;
}

/** Proporción 0–1 como porcentaje con signo: `+2,9 %` (sección 8.3). */
export function percentDelta(proporcion: number, decimales = 1): string {
  if (!Number.isFinite(proporcion)) return DASH;
  const texto = percent(proporcion, decimales);
  return proporcion > 0 ? `+${texto}` : texto;
}

/**
 * Envuelve a cualquier otro formateador para que un dato ausente se pinte como raya.
 * `dash(player.gain, money)` → `12,3 M` o `—`, nunca `0 €`.
 */
export function dash<T>(
  value: T | null | undefined,
  render: (valor: T) => string,
): string {
  return value === null || value === undefined ? DASH : render(value);
}

/** Dos primeras letras del nombre, para la ficha redonda del `PlayerCard`: `Le Normand` → `LE`. */
export function initials(name: string): string {
  return name.trim().slice(0, 2).toUpperCase();
}

/**
 * Nombre acortado para el campo, donde solo caben unos 12 caracteres:
 * `Unai Simón` → `U. Simón`. Si ya cabe, se deja tal cual.
 */
export function shortName(name: string, maximo = 12): string {
  const limpio = name.trim();
  if (limpio.length <= maximo) return limpio;

  const partes = limpio.split(/\s+/);
  if (partes.length < 2) return limpio;

  const [primera, ...resto] = partes;
  return `${primera.charAt(0)}. ${resto.join(" ")}`;
}

/* ================================================================================ fechas == */

/**
 * FECHAS ESCRITAS A MANO, POR LO MISMO QUE LAS CIFRAS
 *   `Intl.DateTimeFormat` y `Intl.RelativeTimeFormat` dan «25 ago» aquí y «25 ago.» allá según la
 *   versión de ICU del equipo. Estas tres funciones nacieron en `pages/Home.tsx` (M8-T01) y suben
 *   aquí en cuanto una segunda pantalla —*Mercado*, con el plazo de cada venta— necesita el mismo
 *   vocabulario: dos copias del mismo calendario acabarían diciendo cosas distintas.
 *
 *   Todas trabajan en la **hora del dispositivo**, que es la del usuario, no la del servidor.
 */

const MESES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const SEGUNDO = 1_000;
const MINUTO = 60 * SEGUNDO;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

function conDosCifras(valor: number): string {
  return valor.toString().padStart(2, "0");
}

/** Los milisegundos de una marca ISO, o `null` si no se puede leer. */
function instante(iso: string): number | null {
  const marca = new Date(iso).getTime();
  return Number.isNaN(marca) ? null : marca;
}

/** `25 ago, 06:15`, en la hora del dispositivo. */
export function fechaCorta(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return DASH;
  return (
    `${fecha.getDate()} ${MESES[fecha.getMonth()]}, ` +
    `${conDosCifras(fecha.getHours())}:${conDosCifras(fecha.getMinutes())}`
  );
}

/** `ahora mismo` · `hace 5 min` · `hace 2 h` · `hace 3 días`. */
export function desdeHace(iso: string, ahora: number = Date.now()): string {
  const marca = instante(iso);
  if (marca === null) return DASH;

  const transcurrido = Math.max(0, ahora - marca);
  if (transcurrido < MINUTO) return "ahora mismo";
  if (transcurrido < HORA) {
    return `hace ${String(Math.floor(transcurrido / MINUTO))} min`;
  }
  if (transcurrido < DIA) {
    return `hace ${String(Math.floor(transcurrido / HORA))} h`;
  }
  const dias = Math.floor(transcurrido / DIA);
  return dias === 1 ? "hace 1 día" : `hace ${String(dias)} días`;
}

/**
 * Cuánto falta hasta `iso`: `3 días` · `18 h` · `45 min` · `menos de 1 min`.
 *
 * Devuelve `null` cuando el plazo **ya ha pasado** o la marca no se puede leer, para que quien lo
 * pinta tenga que decidir qué escribir en su lugar (en *Mercado*, «Expirado»). Una cuenta atrás
 * que se quedara en `0 min` no distinguiría lo que está a punto de cerrarse de lo que ya cerró.
 */
export function restante(
  iso: string,
  ahora: number = Date.now(),
): string | null {
  const marca = instante(iso);
  if (marca === null) return null;

  const falta = marca - ahora;
  if (falta <= 0) return null;
  if (falta < MINUTO) return "menos de 1 min";
  if (falta < HORA) return `${String(Math.floor(falta / MINUTO))} min`;
  if (falta < DIA) return `${String(Math.floor(falta / HORA))} h`;
  const dias = Math.floor(falta / DIA);
  return dias === 1 ? "1 día" : `${String(dias)} días`;
}

/** `true` si esa marca ya ha pasado. Una marca ausente no vence nunca: no tiene plazo. */
export function haVencido(
  iso: string | null | undefined,
  ahora: number = Date.now(),
): boolean {
  if (iso === null || iso === undefined) return false;
  const marca = instante(iso);
  return marca !== null && marca <= ahora;
}

/**
 * DÍAS SUELTOS, SIN HORA Y SIN ZONA HORARIA
 *   La serie de precios de la sección 9.2 no son marcas completas: cada punto es un día suelto
 *   `YYYY-MM-DD`. Pasarlo por `new Date(...)` lo interpretaría como medianoche **UTC**, y en un
 *   equipo al oeste de Greenwich el 11 de agosto se leería como el 10. Por eso el día se parte a
 *   mano cuando llega con esa forma exacta, y solo se cae a `Date` para lo que sí trae hora.
 */
const DIA_SUELTO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Los tres números de un día suelto, o `null` si la cadena no es un día suelto. */
function partesDelDia(dia: string): [number, number, number] | null {
  const encaje = DIA_SUELTO.exec(dia);
  if (encaje === null) return null;
  return [
    Number.parseInt(encaje[1], 10),
    Number.parseInt(encaje[2], 10),
    Number.parseInt(encaje[3], 10),
  ];
}

/** `11 ago`: un día sin hora, para los ejes de las gráficas y las series diarias. */
export function fechaDia(iso: string): string {
  const partes = partesDelDia(iso);
  if (partes !== null) {
    const [, mes, dia] = partes;
    const nombre = MESES[mes - 1];
    return nombre === undefined ? DASH : `${String(dia)} ${nombre}`;
  }

  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return DASH;
  return `${String(fecha.getDate())} ${MESES[fecha.getMonth()]}`;
}

/**
 * Cuántos días enteros hay entre dos días sueltos (`2026-08-11` → `2026-08-25` son 14).
 * `null` si alguno de los dos no es un día suelto: es mejor no decir nada que decir «0 días».
 */
export function diasEntre(desde: string, hasta: string): number | null {
  const uno = partesDelDia(desde);
  const otro = partesDelDia(hasta);
  if (uno === null || otro === null) return null;

  // En UTC los dos, que es donde un día mide siempre 24 h: en hora local, el día del cambio de
  // hora mide 23 o 25 y la resta saldría con decimales.
  const marcaUno = Date.UTC(uno[0], uno[1] - 1, uno[2]);
  const marcaOtro = Date.UTC(otro[0], otro[1] - 1, otro[2]);
  return Math.round((marcaOtro - marcaUno) / DIA);
}

/**
 * Agrupador con el mismo nombre que usa la aceptación de la tarea
 * (`format.money(12_300_000) === "12,3 M"`).
 */
export const format = {
  DASH,
  count,
  money,
  delta,
  points,
  pointsDelta,
  decimal,
  percent,
  percentDelta,
  dash,
  initials,
  shortName,
  fechaCorta,
  fechaDia,
  desdeHace,
  restante,
  haVencido,
  diasEntre,
} as const;
